// Import React & core libraries
import React, { useEffect, useRef, useState } from 'react'
import { logos } from '../partner-logos/partner-logo.js'
import { renderMarkup } from './language/renderMarkup'
// Import Third-party libraries
import { GoogleOAuthProvider } from '@react-oauth/google'
import { useDispatch, useSelector } from 'react-redux'
import './authorisation.css'
import { extractUserData } from './chat/services/extractUserData'

// Import necessary styles and CSS
import '../App.css'

// Import Static assets (images, logos)
// TODO: swap these placeholders for real files once you have them.
// - haliLogoWhite: white/reversed Halisi wordmark for the pink header bar
// - heroPortrait: the right-side model photo behind the login card
import productLogo from '../assets/logos/product_logo_homepage.png'
import neotexLogo from '../assets/logos/neotex_logo.png'
import haliLogoWhite from '../assets/logos/halisi_logo_white.png' // placeholder — replace with white wordmark
import heroPortrait from '../assets/background-image.png' // placeholder — replace with new hero photo

import {
  FaInstagram,
  FaTiktok,
  FaLinkedinIn,
  FaFacebookF,
} from 'react-icons/fa6'

// Import Local components & utilities
import GoogleAuth from './access_management/GoogleAuth'
// import FacebookAuth from './access_management/FacebookAuth'
import { queryAgentEmail } from './access_management/QueryAgent'
import { getDistributorData } from './access_management/GetDistributor'
import AlertMessage from './layout/AlertMessage'
import LanguageSelector from './language/LanguageSelector'
import fetchLanguage from './language/FetchLanguage'
// NOTE: this import path must exactly match the actual filename on disk
// (case-sensitive). If your file is CookieConsent.jsx, change this import
// to './CookieConsent' — a mismatched path is the #1 reason a component
// silently "doesn't work": the build either fails to resolve it or an old
// cached bundle keeps serving a stale version.
import CookieConsent from './CookiesConsent'

// Contact email used across the page for mailto links.
const CONTACT_EMAIL = 'info@neotex.ai'

const Authorisation = ({ goToChatbot }) => {
  const [isConsentChecked, setIsConsentChecked] = useState(false)
  const [apiCallInProgress, setApiCallInProgress] = useState(false)

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID
  const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID
  const environment = process.env.REACT_APP_ENV
  const trial_end_date = process.env.REACT_APP_TRIAL_END_DATE
  const is_trial = process.env.REACT_APP_TRIAL_STATUS

  const dispatch = useDispatch()
  const agentData = useSelector((state) => state.agentData)
  const [trialExpired, setTrialExpired] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [showLoginAlert, setShowLoginAlert] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showPlatformFailureAlert, setPlatformFailureAlert] = useState(false)
  const [translations, setTranslations] = useState(null)
  const [showTrialAlert, setShowTrialAlert] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const cookieConsentRef = useRef(null)

  const TRIAL_END_DATE = new Date(trial_end_date)
  const currentDate = new Date()
  const language = useSelector((state) => state.language)

  const renderLoadingSpinner = () => (
    <div className='loading-spinner-container-auth'>
      <div className='loading-spinner-auth'></div>
    </div>
  )

  useEffect(() => {
    if (currentDate > TRIAL_END_DATE) {
      dispatch({ type: 'SET_TRIAL_EXPIRY', payload: true })
      setTrialExpired(true)
    }
  }, [])

  useEffect(() => {
    const loadTranslations = async () => {
      const data = await fetchLanguage('', language)
      setTranslations(data)
      dispatch({ type: 'SET_TRANSLATIONS_DATA', payload: data })
    }
    loadTranslations()
  }, [language])

  function getTranslationText(keyPath, data = translations, lang = language) {
    const validLangs = ['EN', 'FR', 'NL', 'SW']
    const normalizedLang = String(lang || 'EN')
      .trim()
      .toUpperCase()
    const selectedLang = validLangs.includes(normalizedLang)
      ? normalizedLang
      : 'EN'

    const keys = keyPath.split('.')
    let result = data
    for (const key of keys) {
      result = result?.[key]
      if (Array.isArray(result)) result = result[0]
    }

    if (result && typeof result === 'object') {
      return result[selectedLang] ?? result.EN ?? 'Text not found'
    }

    return result || 'Text not found'
  }

  const productDiscoveryMessage = getTranslationText(
    'authorization.productDiscovery.text',
  )
  const personalizedHairRecommendationsMessage = getTranslationText(
    'authorization.personalizedHairRecommendations.text',
  )
  const welcomeMessage = getTranslationText('authorization.welcomeMessage.text')
  const poweredByAIText = getTranslationText('authorization.poweredByAI.text')
  const privacyPolicyConsentText = getTranslationText(
    'authorization.privacyPolicyConsent.text',
  )
  const privacyPolicy = getTranslationText('authorization.privacyPolicy.text')
  const privacyPolicyURL = getTranslationText(
    'authorization.privacyPolicyURL.text',
  )
  const homeText = getTranslationText('authorization.home.text')
  const contactText = getTranslationText('authorization.contact.text')
  const copyrightNotice = getTranslationText(
    'authorization.copyrightNotice.text',
  )
  const confirmationAlertText = getTranslationText(
    'authorization.confirmationAlert.text',
  )
  const confirmationAlertSubtext = getTranslationText(
    'authorization.confirmationAlert.subtext',
  )
  const loginAlertText = getTranslationText('authorization.loginAlert.text')
  const loginFailureAlertText = getTranslationText(
    'authorization.loginFailureAlert.text',
  )
  const authenticationSuccessAlertText = getTranslationText(
    'authorization.authenticationSuccessAlert.text',
  )
  const termsAndConditionsConsentText = getTranslationText(
    'authorization.termsAndConditionsConsent.text',
  )
  const trialExpiryAlertText = getTranslationText(
    'authorization.trialExpiryAlert.text',
  )

  // Hero Section
  const heroTitle = getTranslationText('heroSection.title')
  const heroSubtext = getTranslationText('heroSection.subtext')
  const heroTrustText = getTranslationText('heroSection.trustText')

  // Footer
  const footerFollowUs = getTranslationText('footer.followUs')
  const footerPrivacyPolicy = getTranslationText('footer.privacyPolicy')
  const footerCookiePolicy = getTranslationText('footer.cookiePolicy')
  const footerHowAIWorks = getTranslationText('footer.howAIWorks')
  const footerChangeCookieSettings = getTranslationText(
    'footer.changeCookieSettings',
  )
  const footerContactUs = getTranslationText('footer.contactUs')
  const footerProductDevelopedBy = getTranslationText(
    'footer.productDevelopedBy',
  )
  const orContinueWithText = getTranslationText(
    'authorization.orContinueWith.text',
  )

  const isPlatformAuthFailure = () => setPlatformFailureAlert(true)

  const isPlatformAuthSuccess = async (data) => {
    await queryHalisiDB(data)
  }

  const queryHalisiDB = async (data) => {
    let isMounted = true
    dispatch({ type: 'SET_EXISTING_USER_PROFILE', payload: null })

    try {
      const response = await queryAgentEmail(
        data.agentEmail,
        environment,
        is_trial,
        dispatch,
        data,
      )
      if (trialExpired) {
        setShowTrialAlert(true)
        return
      }

      //QueryExistingUserData
      const existingUserPayload = {
        agent_id: response?.agent_id ?? data?.agent_id,
        email_id: data?.agentEmail,
        env: environment,
        is_trial: is_trial,
      }

      const existingUserProfile = await extractUserData(existingUserPayload)

      const savedLanguage = String(
        existingUserProfile?.user_data?.language || '',
      )
        .trim()
        .toLowerCase()

      if (['en', 'fr', 'nl', 'sw'].includes(savedLanguage)) {
        dispatch({
          type: 'SET_LANGUAGE',
          payload: savedLanguage,
        })
      }

      dispatch({
        type: 'SET_EXISTING_USER_PROFILE',
        payload: existingUserProfile,
      })

      await getDistributorData(
        response.distributor_id,
        environment,
        is_trial,
        dispatch,
      )
      if (isMounted) setShowSuccessAlert(true)
    } catch (error) {
      dispatch({ type: 'SET_AGENT_DATA', payload: data })
      goToChatbot()
    } finally {
      if (isMounted) setIsLoading(false)
    }

    return () => {
      isMounted = false
    }
  }

  // Called whenever cookie consent changes (first accept/reject, or a later
  // edit via "Change cookie settings"). Gate any analytics/marketing scripts
  // behind these flags — do not fire them until `analytics`/`marketing` is true.
  const handleConsentChange = (prefs) => {
    dispatch({ type: 'SET_COOKIE_CONSENT', payload: prefs })
  }

  return (
    <>
      <div className='halisi-page'>
        <div className='halisi-page__topbar'>
          <div className='halisi-page__brand'>
            <a
              href='https://halisi.ai/cosmetics'
              target='_blank'
              rel='noopener noreferrer'
            >
              <img src={haliLogoWhite} alt='Halisi Cosmetics' />
            </a>
          </div>
          <LanguageSelector />
        </div>

        <div className='halisi-page__body'>
          {/* ── Hero copy ── */}
          <div className='halisi-hero__text'>
            <h1>{renderMarkup(heroTitle)}</h1>
            <p>{renderMarkup(heroSubtext)}</p>
          </div>

          {/* ── Trust logos: sits under the hero copy on desktop, but
              drops below the login card on mobile via grid order. ── */}
          <div className='halisi-trust'>
            <p className='halisi-hero__trust-title'>{heroTrustText}</p>
            <div className='halisi-hero__trust-strip'>
              {logos.map((brand) => (
                <img
                  key={brand.id}
                  style={brand.style}
                  src={brand.image}
                  alt={brand.name}
                />
              ))}
            </div>
          </div>

          {/* ── Login card over hero photo ── */}
          <div className='halisi-auth-panel'>
            <div className='halisi-card'>
              <div className='halisi-card__logo-wrap'>
                <img src={productLogo} alt='Product Logo' />
              </div>

              <div className='halisi-card__badge'>
                <span className='halisi-card__badge-dot' />
                {productDiscoveryMessage}
              </div>

              <p className='halisi-card__tagline'>
                {personalizedHairRecommendationsMessage}
              </p>

              <p className='halisi-card__welcome'>{welcomeMessage}</p>

              <div
                className='halisi-consent'
                onClick={() => setIsConsentChecked((prev) => !prev)}
              >
                <input
                  type='checkbox'
                  className='halisi-consent__input'
                  checked={isConsentChecked}
                  onChange={() => setIsConsentChecked((prev) => !prev)}
                  id='consent-checkbox'
                />
                <div
                  className={`halisi-consent__box${isConsentChecked ? ' checked' : ''}`}
                >
                  <svg
                    className='halisi-consent__checkmark'
                    viewBox='0 0 10 10'
                    fill='none'
                  >
                    <path
                      d='M1.5 5L4 7.5L8.5 2.5'
                      stroke='white'
                      strokeWidth='1.6'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <span className='halisi-consent__label'>
                  {privacyPolicyConsentText}&nbsp;
                  <a
                    href='https://halisi.ai/cosmetics-privacy'
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={(e) => e.stopPropagation()}
                  >
                    {privacyPolicy}
                  </a>
                </span>
              </div>

              <div className='halisi-auth-buttons'>
                <GoogleOAuthProvider clientId={googleClientId}>
                  <GoogleAuth
                    isPlatformAuthSuccess={isPlatformAuthSuccess}
                    isPlatformAuthFailure={isPlatformAuthFailure}
                    isConsentChecked={isConsentChecked}
                    consentMessage={termsAndConditionsConsentText}
                    setIsLoading={setIsLoading}
                  />
                </GoogleOAuthProvider>

                {/* <div className='halisi-divider'>
                  <div className='halisi-divider__line' />
                  <span className='halisi-divider__label'>
                    {orContinueWithText}
                  </span>
                  <div className='halisi-divider__line' />
                </div>

                <FacebookAuth
                  facebookAppId={facebookAppId}
                  isPlatformAuthSuccess={isPlatformAuthSuccess}
                  isPlatformAuthFailure={isPlatformAuthFailure}
                  isConsentChecked={isConsentChecked}
                  consentMessage={termsAndConditionsConsentText}
                  setIsLoading={setIsLoading}
                /> */}
              </div>

              {isLoading && (
                <div className='halisi-spinner-wrap'>
                  {renderLoadingSpinner()}
                </div>
              )}

              <div className='halisi-card__footer'>
                <p className='halisi-card__powered'>{poweredByAIText}</p>
                <div className='halisi-card__neotex'>
                  <img src={neotexLogo} alt='Neotex Logo' />
                </div>
                <div className='halisi-card__links'>
                  <a href='#' target='_blank' rel='noopener noreferrer'>
                    {homeText}
                  </a>
                  <span className='dot'>•</span>
                  <a
                    href='https://halisi.ai/halisi-cosmetics/privacy'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {privacyPolicy}
                  </a>
                  <span className='dot'>•</span>
                  <a href={`mailto:${CONTACT_EMAIL}`}>{footerContactUs}</a>
                </div>
                <p className='halisi-card__copyright'>{copyrightNotice}</p>
              </div>
            </div>
          </div>

          {/* ── Follow us: last item in the left column on desktop,
              last item overall on mobile. ── */}
          <div className='halisi-follow'>
            <div className='halisi-hero__follow-row'>
              <div className='halisi-hero__follow-copy'>
                <p className='halisi-hero__follow-title'>{footerFollowUs}</p>
                <p className='halisi-hero__handle'>@halisicosmetics</p>
              </div>
              <div className='halisi-hero__socials'>
                <a href='#' aria-label='Instagram'>
                  <FaInstagram color='black' size={22} />
                </a>
                <a href='#' aria-label='TikTok'>
                  <FaTiktok color='black' size={22} />
                </a>
                <a href='#' aria-label='LinkedIn'>
                  <FaLinkedinIn color='black' size={22} />
                </a>
                <a href='#' aria-label='Facebook'>
                  <FaFacebookF color='black' size={22} />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Page footer nav ── */}
        <div className='halisi-page__footer'>
          <div className='halisi-page__footer-links'>
            <a
              href='https://halisi.ai/cosmetics-privacy'
              target='_blank'
              rel='noopener noreferrer'
            >
              {footerPrivacyPolicy}
            </a>
            <a
              href='https://halisi.ai/cosmetics-cookies'
              target='_blank'
              rel='noopener noreferrer'
            >
              {footerCookiePolicy}
            </a>
            <a
              href='https://halisi.ai/cosmetics'
              target='_blank'
              rel='noopener noreferrer'
            >
              {footerHowAIWorks}
            </a>
            <button
              type='button'
              onClick={() => cookieConsentRef.current?.openPreferences()}
            >
              {footerChangeCookieSettings}
            </button>
            <a href={`mailto:${CONTACT_EMAIL}`}>{footerContactUs}</a>
          </div>
          <div className='halisi-page__footer-by'>
            <span>{footerProductDevelopedBy}</span>
            <img src={neotexLogo} alt='Neotex' />
          </div>
        </div>
      </div>

      {/* ── Cookie consent banner / preferences modal ── */}
      <CookieConsent
        ref={cookieConsentRef}
        onConsentChange={handleConsentChange}
      />

      {/* Alerts */}
      <>
        {showAlert && (
          <AlertMessage
            message={confirmationAlertText}
            subtext={confirmationAlertSubtext}
            onConfirm={() => setShowAlert(false)}
            onCancel={() => setShowAlert(false)}
          />
        )}
        {showLoginAlert && (
          <AlertMessage
            message={loginAlertText}
            onConfirm={() => setShowLoginAlert(false)}
          />
        )}
        {showPlatformFailureAlert && (
          <AlertMessage
            message={loginFailureAlertText}
            onConfirm={() => setPlatformFailureAlert(false)}
          />
        )}
        {showSuccessAlert && (
          <AlertMessage
            message={authenticationSuccessAlertText}
            onConfirm={async () => {
              setShowSuccessAlert(false)
              if (agentData.status === 'User') {
                goToChatbot()
              } else {
                goToChatbot()
              }
            }}
          />
        )}
        {showTrialAlert && (
          <AlertMessage
            message={trialExpiryAlertText}
            onConfirm={async () => setShowTrialAlert(false)}
          />
        )}
      </>
    </>
  )
}

export default Authorisation